import { useEffect, useMemo, useRef, useState, type CSSProperties, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { useExpressMode } from './useExpressMode';
import { useScenarioPromptOptions, useScenarioAttributes } from '../../../features/scenarios/hooks';
import type { ScenarioAttributeNode } from '../../../features/scenarios/api';
import { StepIndicator } from './components/StepIndicator';
import { StepAsinSearch } from './steps/StepAsinSearch';
import { StepSettings } from './steps/StepSettings';
import { StepGenerating } from './steps/StepGenerating';
import { StepExport } from './steps/StepExport';
import styles from './ExpressModeModal.module.css';

interface ExpressModeModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialAsin?: string | null;
  onInitialAsinConsumed?: () => void;
}

const STEP_LABELS = ['商品選択', '設定', '絵コンテ', '動画'];

export function ExpressModeModal({ isOpen, onClose, initialAsin, onInitialAsinConsumed }: ExpressModeModalProps) {
  const express = useExpressMode({ initialAsin: initialAsin ?? undefined, onInitialAsinConsumed, enabled: isOpen });
  const { data: promptOptions } = useScenarioPromptOptions();
  const { data: scenarioAttributes } = useScenarioAttributes();

  // 絵コンテ拡大プレビュー: PC=右ペインに常時、スマホ=タップで全画面。
  const [focusedStoryboardId, setFocusedStoryboardId] = useState<string | null>(null);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);

  // 先頭の「完成済み」絵コンテ。1 枚でも完成すれば右ペインを開いてプレビューする。
  const firstDoneStoryboardId = useMemo(() => {
    return express.scenarios.find(
      (s) => express.scenarioAssetStatus[s.id]?.storyboardStatus === 'done',
    )?.id ?? null;
  }, [express.scenarios, express.scenarioAssetStatus]);

  // 1 枚目が完成次第プレビュー対象に。生成中でも完成したものを順次差し替え可能。
  useEffect(() => {
    if (express.step !== 3 || !firstDoneStoryboardId) return;
    setFocusedStoryboardId((prev) => {
      if (prev && express.scenarioAssetStatus[prev]?.storyboardStatus === 'done') return prev;
      return firstDoneStoryboardId;
    });
  }, [express.step, firstDoneStoryboardId, express.scenarioAssetStatus]);

  // プレビュー対象の切り替え (右ペインの内容更新のみ)。ホバー / キーボードフォーカスで使う。
  // 全画面 (モバイル) は開かない。
  const handlePreviewStoryboard = (id: string) => {
    setFocusedStoryboardId(id);
  };

  // モバイルで絵コンテを全画面表示する (ホバー不可のため明示的なタップ導線)。
  const handleOpenMobilePreview = (id: string) => {
    setFocusedStoryboardId(id);
    setMobilePreviewOpen(true);
  };

  // 絵コンテ Step かつ 1 枚でも完成していれば右ペイン (拡大プレビュー) を開く。
  // 全部の完成 (storyboardsReady) を待たず、生成途中でも先に出す。
  const isStoryboardSelect = express.step === 3 && firstDoneStoryboardId !== null;
  const focusedStoryboardUrl = focusedStoryboardId
    ? express.scenarioAssetStatus[focusedStoryboardId]?.storyboardImageUrl ?? null
    : null;

  // 前/次の絵コンテへめくるためのナビゲーション。完成済み (done) の案だけを順に辿る。
  const doneStoryboardIds = useMemo(
    () => express.scenarios
      .filter((s) => express.scenarioAssetStatus[s.id]?.storyboardStatus === 'done')
      .map((s) => s.id),
    [express.scenarios, express.scenarioAssetStatus],
  );
  const focusedIndex = focusedStoryboardId ? doneStoryboardIds.indexOf(focusedStoryboardId) : -1;
  const goPrevStoryboard = () => {
    if (focusedIndex > 0) setFocusedStoryboardId(doneStoryboardIds[focusedIndex - 1]);
  };
  const goNextStoryboard = () => {
    if (focusedIndex >= 0 && focusedIndex < doneStoryboardIds.length - 1) {
      setFocusedStoryboardId(doneStoryboardIds[focusedIndex + 1]);
    }
  };

  // 左右キーで前/次の絵コンテへめくる (右ペイン表示中 / モバイル全画面中)。
  useEffect(() => {
    if (!isStoryboardSelect && !mobilePreviewOpen) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrevStoryboard(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNextStoryboard(); }
      else if (e.key === 'Enter') {
        // カード / 矢印ボタン自身がフォーカスされている場合はそれぞれの活性化に任せ二重トグルを防ぐ。
        if (target && target.closest('button, [role="button"]')) return;
        if (focusedStoryboardId) { e.preventDefault(); express.selectScenario(focusedStoryboardId); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // goPrev/goNext は focusedIndex / doneStoryboardIds に依存する closure なのでそれらを deps に含める
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStoryboardSelect, mobilePreviewOpen, focusedIndex, doneStoryboardIds, focusedStoryboardId, express.selectScenario]);
  // 前/次ボタン (右ペイン=fixed / モバイル全画面=absolute で配置を切り替える)。
  const renderStoryboardNav = (variant: 'pane' | 'fullscreen') => {
    if (focusedIndex < 0 || doneStoryboardIds.length <= 1) return null;
    const ctx = variant === 'pane' ? styles.storyboardNavPane : styles.storyboardNavFullscreen;
    return (
      <>
        <button
          type="button"
          className={`${styles.storyboardNavBtn} ${ctx} ${styles.storyboardNavPrev}`}
          onClick={(e) => { e.stopPropagation(); goPrevStoryboard(); }}
          disabled={focusedIndex <= 0}
          aria-label="前の絵コンテ"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          type="button"
          className={`${styles.storyboardNavBtn} ${ctx} ${styles.storyboardNavNext}`}
          onClick={(e) => { e.stopPropagation(); goNextStoryboard(); }}
          disabled={focusedIndex >= doneStoryboardIds.length - 1}
          aria-label="次の絵コンテ"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </>
    );
  };

  // 右ペインのズーム (1.0〜3.0)。カードを切り替えても倍率は維持する。
  const [zoom, setZoom] = useState(1);
  const zoomIn = () => setZoom((z) => Math.min(3, Math.round((z + 0.5) * 10) / 10));
  const zoomOut = () => setZoom((z) => Math.max(1, Math.round((z - 0.5) * 10) / 10));

  // 表示する絵コンテが変わったら、右ペインを一番上 (左上) へスクロールする。
  const previewPaneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    previewPaneRef.current?.scrollTo({ top: 0, left: 0 });
  }, [focusedStoryboardId]);

  // 絵コンテの実アスペクト比 (幅/高さ)。右ペイン幅 = 100dvh × aspect とすることで、
  // ペイン幅が絵コンテ幅にぴったり一致し、左右の黒帯が出ず・高さも収まる。
  const [storyboardAspect, setStoryboardAspect] = useState(0.6667);
  const handleStoryboardImgLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setStoryboardAspect(img.naturalWidth / img.naturalHeight);
    }
  };

  // Lock body scroll & set white background for safe-area on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.backgroundColor = '#ffffff';
      return () => {
        document.body.style.overflow = '';
        document.body.style.backgroundColor = '';
      };
    }
  }, [isOpen]);

  // Parse tone options into hierarchical groups。
  // 'tone' が新ツリー経路に切替済みなら属性ツリーの tone フィールドから構築（送信値=option key）。
  // 未切替なら旧 promptOptions.tone から構築（送信値=旧 value）。
  const toneGroups = useMemo(() => {
    const treeEnabled = (scenarioAttributes?.categories_enabled || []).includes('tone');
    if (treeEnabled) {
      const findByKey = (nodes: ScenarioAttributeNode[] | undefined, key: string): ScenarioAttributeNode | undefined => {
        for (const n of nodes || []) {
          if (n.key === key) return n;
          const found = findByKey(n.children, key);
          if (found) return found;
        }
        return undefined;
      };
      const field = findByKey(scenarioAttributes?.items, 'tone');
      if (field?.children?.length) {
        const groups: Array<{ id: string; label: string; children: Array<{ value: string; label: string }> }> = [];
        const flat: Array<{ value: string; label: string }> = [];
        for (const child of field.children) {
          if (child.node_type === 'group' && child.children?.length) {
            const opts = child.children
              .filter((o) => o.node_type === 'option')
              .map((o) => ({ value: o.key, label: o.label }));
            if (opts.length) groups.push({ id: child.id, label: child.label, children: opts });
          } else if (child.node_type === 'option') {
            flat.push({ value: child.key, label: child.label });
          }
        }
        if (flat.length) groups.push({ id: 'ungrouped', label: 'その他', children: flat });
        return groups;
      }
    }

    if (!promptOptions) return [];

    const toneData = promptOptions.tone;
    if (!toneData) return [];

    if (toneData.groups && toneData.groups.length > 0) {
      return toneData.groups
        .filter((group): group is typeof group & { children: Array<{ value: string; label: string }> } =>
          'children' in group && Array.isArray(group.children) && group.children.length > 0
        )
        .map(group => ({
          id: group.id,
          label: group.label,
          children: group.children.map(opt => ({ value: opt.value, label: opt.label })),
        }));
    }

    // Fallback: wrap flat options into a single group
    if (toneData.options && toneData.options.length > 0) {
      return [{
        id: 'all',
        label: 'すべて',
        children: toneData.options.map(opt => ({ value: opt.value, label: opt.label })),
      }];
    }

    return [];
  }, [promptOptions, scenarioAttributes]);

  const handleClose = () => {
    express.reset();
    onClose();
  };

  if (!isOpen) return null;

  const displayStep = express.step;

  const confirmAndNavigate = (targetStep: 1 | 2 | 3 | 4) => {
    if (express.isAutoGenerating) {
      const confirmed = window.confirm(
        '自動生成中です。戻ると生成中のデータは破棄されます。よろしいですか？'
      );
      if (!confirmed) return;
    }
    express.goToStep(targetStep);
  };

  const handleStepClick = (step: number) => {
    if (express.isGenerating || express.isExporting) return;
    if (step <= express.maxStepReached && step !== displayStep) {
      confirmAndNavigate(step as 1 | 2 | 3 | 4);
    }
  };

  const handleBack = () => {
    if (express.isGenerating || express.isExporting) return;
    if (express.step <= 1) return;
    const prevStep = (express.step - 1) as 1 | 2 | 3 | 4;
    confirmAndNavigate(prevStep);
  };

  return createPortal(
    <div
      className={`${styles.overlay} ${isStoryboardSelect ? styles.overlaySplit : ''}`}
      style={{ ['--sb-aspect']: String(storyboardAspect), ['--sb-zoom']: String(zoom) } as CSSProperties}
    >
      <div className={styles.modal}>
        {express.step > 1 && (
          <button className={styles.modalBackButton} onClick={handleBack} type="button">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
        <button className={styles.closeButton} onClick={handleClose} type="button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className={styles.header}>
          <h1 className={styles.modalTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            動画特急モード
          </h1>
          <StepIndicator currentStep={displayStep} totalSteps={4} labels={STEP_LABELS} maxStepReached={express.maxStepReached} onStepClick={(express.isGenerating || express.isExporting) ? undefined : handleStepClick} />
        </div>

        <div className={styles.body}>
          {express.step === 1 && (
            <StepAsinSearch
              asin={express.asin}
              accountId={express.accountId}
              amazonAccounts={express.amazonAccounts}
              productInfo={express.productInfo}
              isSearching={express.isSearching}
              isRegistering={express.isRegistering}
              registered={express.registered}
              searchError={express.searchError}
              onAsinChange={express.setAsin}
              onAccountChange={express.setAccountId}
              onSearch={express.searchAsin}
              onRegister={express.registerProduct}
              onSelectManualProduct={express.selectManualProduct}
              onClose={handleClose}
            />
          )}

          {express.step === 2 && (
            <StepSettings
              tone={express.tone}
              toneTouched={express.toneTouched}
              recommendedTone={express.recommendedTone}
              aspectRatio={express.aspectRatio}
              scenarioCount={express.scenarioCount}
              duration={express.duration}
              selectedPresetActors={express.selectedPresetActors}
              toneGroups={toneGroups}
              onToneChange={express.setTone}
              onAspectRatioChange={express.setAspectRatio}
              onScenarioCountChange={express.setScenarioCount}
              onDurationChange={express.setDuration}
              onPresetActorsChange={express.setSelectedPresetActors}
              onGenerate={express.startGeneration}
              onFullGenerate={express.startFullGeneration}
              productTitle={express.productInfo?.title || express.manualProductTitle || undefined}
              productImageUrl={express.productInfo?.main_image_url || express.manualProductImageUrl || undefined}
            />
          )}

          {/* Step 3 絵コンテ: シナリオ生成ローディング → 絵コンテ生成 → 選択 を 1 つで担う */}
          {express.step === 3 && (
            <StepGenerating
              isGeneratingScenarios={express.isGenerating}
              isAutoGenerating={express.isAutoGenerating}
              storyboardsReady={express.storyboardsReady}
              scenarios={express.scenarios.map(s => ({ id: s.id, title: s.title }))}
              scenarioAssetStatus={express.scenarioAssetStatus}
              expectedCount={express.scenarioCount}
              selectedIds={express.selectedScenarioIds}
              focusedId={focusedStoryboardId}
              videoDurationSec={Math.max(4, Math.min(15, Number(express.duration) || 15))}
              aspectRatio={express.aspectRatio}
              onToggleSelect={express.selectScenario}
              onPreview={handlePreviewStoryboard}
              onOpenMobilePreview={handleOpenMobilePreview}
              onGenerateVideo={express.startVideoGeneration}
            />
          )}

          {express.step === 4 && (
            <StepExport
              scenarioId={express.selectedScenarioIds[0] ?? null}
              allScenarioIds={express.selectedScenarioIds.length > 0 ? express.selectedScenarioIds : express.scenarios.map(s => s.id)}
              isExporting={express.isExporting}
              exportComplete={express.exportComplete}
              exportStatus={express.exportStatus}
              pendingRedownload={express.pendingRedownload}
              mp4DownloadUrl={express.mp4DownloadUrl}
              mp4Generating={express.mp4Generating}
              isAutoGenerating={express.isAutoGenerating}
              videosDone={express.assetCounts.videosDone}
              videosTotal={express.assetCounts.total}
              onExportZip={express.exportMp4Zip}
              onExportConcat={express.exportMp4Concat}
              onBackToScenarios={() => express.backToScenarioSelect()}
              onClose={handleClose}
            />
          )}
        </div>
      </div>

      {/* PC: 右側の大画面プレビュー (絵コンテ画像のみ・黒背景・全面)。Step 4 のみ表示 */}
      {isStoryboardSelect && (
        <div className={styles.storyboardPreviewPane} ref={previewPaneRef}>
          {focusedStoryboardUrl ? (
            <>
              {/* ホバーで出る拡大縮小コントロール */}
              <div className={styles.storyboardPreviewControls}>
                <button
                  type="button"
                  className={styles.storyboardPreviewZoomBtn}
                  onClick={zoomOut}
                  disabled={zoom <= 1}
                  aria-label="縮小"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={styles.storyboardPreviewZoomBtn}
                  onClick={zoomIn}
                  disabled={zoom >= 3}
                  aria-label="拡大"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
              </div>
              {/* 画像右上の選択状態バッジ (クリックで選択トグル) */}
              {focusedStoryboardId && (
                <button
                  type="button"
                  className={`${styles.storyboardPreviewBadge} ${express.selectedScenarioIds.includes(focusedStoryboardId) ? styles.storyboardStatusBadgeOn : ''}`}
                  onClick={() => express.selectScenario(focusedStoryboardId)}
                  aria-pressed={express.selectedScenarioIds.includes(focusedStoryboardId)}
                >
                  {express.selectedScenarioIds.includes(focusedStoryboardId) ? '選択中' : '未選択'}
                </button>
              )}
              <img
                src={focusedStoryboardUrl}
                alt="絵コンテ"
                className={styles.storyboardPreviewImg}
                onLoad={handleStoryboardImgLoad}
              />
              {/* 前/次の絵コンテへめくる */}
              {renderStoryboardNav('pane')}
            </>
          ) : (
            <div className={styles.storyboardPreviewEmpty}>絵コンテを選ぶと拡大表示されます</div>
          )}
        </div>
      )}

      {/* スマホ: タップで全画面プレビュー */}
      {mobilePreviewOpen && focusedStoryboardUrl && (
        <div
          className={styles.storyboardPreviewFullscreen}
          onClick={() => setMobilePreviewOpen(false)}
          role="dialog"
        >
          <button
            type="button"
            className={styles.storyboardPreviewClose}
            onClick={(e) => { e.stopPropagation(); setMobilePreviewOpen(false); }}
            aria-label="閉じる"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <img src={focusedStoryboardUrl} alt="絵コンテ" className={styles.storyboardPreviewFullscreenImg} />
          {/* 前/次の絵コンテへめくる */}
          {renderStoryboardNav('fullscreen')}
        </div>
      )}
    </div>,
    document.body
  );
}
